import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Shield, Trash2, Pencil, X, UserCog } from "lucide-react";
import { Switch } from "../components/ui/switch";
import api, { formatApiErrorDetail } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { fmtDate } from "../lib/format";

function EditModal({ target, onClose, onSaved }) {
  const [name, setName] = useState(target.name || "");
  const [email, setEmail] = useState(target.email || "");
  const [role, setRole] = useState(target.role || "user");
  const [password, setPassword] = useState("");
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      const payload = { name, email, role };
      if (password) payload.password = password;
      const { data } = await api.put(`/admin/users/${target.id}`, payload);
      toast.success("Konto aktualisiert");
      onSaved(data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[2000] bg-black/80 backdrop-blur-sm flex items-end sm:items-center justify-center" data-testid="edit-user-modal">
      <div className="bg-[#0A0A0A] border-t sm:border border-white/10 rounded-t-3xl sm:rounded-3xl w-full max-w-lg p-6">
        <div className="flex items-center justify-between mb-6">
          <h2 className="font-display font-black text-2xl tracking-tighter">Konto bearbeiten</h2>
          <button onClick={onClose} className="w-10 h-10 rounded-full bg-[#1A1A1A] border border-white/10 flex items-center justify-center">
            <X className="w-5 h-5" />
          </button>
        </div>
        <div className="space-y-4">
          <div>
            <label className="data-label block mb-2">Name</label>
            <input data-testid="edit-name" value={name} onChange={(e) => setName(e.target.value)} className="w-full bg-[#121212] border border-white/10 rounded-2xl px-4 py-3 focus:border-[#CCFF00] focus:outline-none" />
          </div>
          <div>
            <label className="data-label block mb-2">E-Mail</label>
            <input data-testid="edit-email" value={email} onChange={(e) => setEmail(e.target.value)} className="w-full bg-[#121212] border border-white/10 rounded-2xl px-4 py-3 focus:border-[#CCFF00] focus:outline-none" />
          </div>
          <div>
            <label className="data-label block mb-2">Neues Passwort (optional)</label>
            <input data-testid="edit-password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="leer lassen zum Beibehalten" className="w-full bg-[#121212] border border-white/10 rounded-2xl px-4 py-3 placeholder-neutral-600 focus:border-[#CCFF00] focus:outline-none" />
          </div>
          <div className="flex items-center justify-between bg-[#121212] border border-white/10 rounded-2xl px-4 py-3">
            <div>
              <div className="font-bold text-sm">Administrator</div>
              <div className="text-xs text-neutral-500">Voller Zugriff & Verwaltung</div>
            </div>
            <Switch data-testid="edit-role-switch" checked={role === "admin"} onCheckedChange={(v) => setRole(v ? "admin" : "user")} />
          </div>
          <button data-testid="edit-save" onClick={save} disabled={saving} className="w-full bg-[#CCFF00] text-black font-display font-black text-lg rounded-full py-4 active:scale-95 transition-transform disabled:opacity-60">
            {saving ? "Speichern..." : "Änderungen speichern"}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function Admin() {
  const { user } = useAuth();
  const [users, setUsers] = useState([]);
  const [regEnabled, setRegEnabled] = useState(true);
  const [editing, setEditing] = useState(null);

  const load = () => {
    api.get("/admin/users").then(({ data }) => setUsers(data)).catch(() => {});
    api.get("/admin/settings").then(({ data }) => setRegEnabled(data.registration_enabled)).catch(() => {});
  };

  useEffect(() => {
    load();
  }, []);

  const toggleReg = async (val) => {
    setRegEnabled(val);
    try {
      await api.put("/admin/settings/registration", { enabled: val });
      toast.success(val ? "Registrierung aktiviert" : "Registrierung deaktiviert");
    } catch {
      setRegEnabled(!val);
      toast.error("Änderung fehlgeschlagen");
    }
  };

  const del = async (u) => {
    if (!window.confirm(`Konto von ${u.name} wirklich löschen? Alle Läufe werden entfernt.`)) return;
    try {
      await api.delete(`/admin/users/${u.id}`);
      setUsers((prev) => prev.filter((x) => x.id !== u.id));
      toast.success("Konto gelöscht");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Löschen fehlgeschlagen");
    }
  };

  const onSaved = (updated) => {
    setUsers((prev) => prev.map((u) => (u.id === updated.id ? { ...u, ...updated } : u)));
    setEditing(null);
  };

  return (
    <div className="max-w-lg mx-auto px-5 pt-safe">
      <div className="flex items-center gap-2 mb-6">
        <Shield className="w-6 h-6 text-[#CCFF00]" />
        <h1 className="font-display font-black text-4xl tracking-tighter">Admin</h1>
      </div>

      <div className="bg-[#121212] border border-white/10 rounded-2xl p-5 mb-6 flex items-center justify-between" data-testid="registration-toggle-card">
        <div>
          <div className="font-display font-black text-lg tracking-tighter">Registrierung</div>
          <div className="text-sm text-neutral-500">Neue Nutzer {regEnabled ? "können" : "können nicht"} beitreten</div>
        </div>
        <Switch data-testid="registration-switch" checked={regEnabled} onCheckedChange={toggleReg} />
      </div>

      <div className="flex items-center gap-2 mb-3">
        <UserCog className="w-4 h-4 text-neutral-500" />
        <span className="data-label">Konten ({users.length})</span>
      </div>

      <div className="space-y-3" data-testid="users-list">
        {users.map((u) => (
          <div key={u.id} data-testid={`user-item-${u.id}`} className="bg-[#121212] border border-white/10 rounded-2xl p-4 flex items-center gap-3">
            <div className="w-11 h-11 rounded-full bg-[#1A1A1A] border border-white/10 flex items-center justify-center font-display font-black text-lg shrink-0">
              {u.name?.[0]?.toUpperCase()}
            </div>
            <div className="flex-1 min-w-0">
              <div className="font-bold truncate flex items-center gap-2">
                {u.name}
                {u.role === "admin" && <Shield className="w-3.5 h-3.5 text-[#CCFF00] shrink-0" />}
              </div>
              <div className="text-xs text-neutral-500 truncate">{u.email}</div>
              <div className="text-xs text-neutral-600 mt-0.5">{u.run_count} Läufe · seit {fmtDate(u.created_at)}</div>
            </div>
            <button onClick={() => setEditing(u)} data-testid={`edit-user-${u.id}`} className="w-9 h-9 rounded-full bg-[#1A1A1A] border border-white/10 flex items-center justify-center text-neutral-300 active:scale-95 transition-transform">
              <Pencil className="w-4 h-4" />
            </button>
            {u.id !== user.id && (
              <button onClick={() => del(u)} data-testid={`delete-user-${u.id}`} className="w-9 h-9 rounded-full bg-[#1A1A1A] border border-white/10 flex items-center justify-center text-[#FF3366] active:scale-95 transition-transform">
                <Trash2 className="w-4 h-4" />
              </button>
            )}
          </div>
        ))}
      </div>

      {editing && <EditModal target={editing} onClose={() => setEditing(null)} onSaved={onSaved} />}
    </div>
  );
}
